'use client';

// Lịch chọn ngày nhận/trả phòng cho khối "Đặt phòng" ở /sale (thay <input type="date"> native —
// native KHÔNG vẽ được style riêng từng ngày nên không thể hiện ngày đã có khách/gạch ngang). Lấy dữ
// liệu trống/bận qua GET /api/sale/timeline (công khai, không cần đăng nhập, đã tự tính cả booking thật
// lẫn draft đang giữ chỗ của khách khác — xem lib/sale-hold.ts blocksAvailability()).
//
// Hành vi chọn: bấm 1 ngày trống = bắt đầu chọn Nhận phòng (tạm đặt Trả phòng = Nhận phòng+1 đêm để
// khối báo giá vẫn chạy được trong lúc chờ). Bấm ngày trống SAU đó = chốt Trả phòng — NẾU có đêm nào ở
// giữa đã bị khoá thì huỷ, coi ngày vừa bấm là Nhận phòng MỚI (giống Airbnb/Booking.com).

import { useEffect, useMemo, useState } from 'react';
import { fmtDate, todayISO } from '@/lib/utils';
import { logPublicClientError } from '@/lib/client-error-logger';

const MAX_MONTHS_AHEAD = 18; // khớp giới hạn far_future server-side (lib/sale-booking.ts)
const WEEKDAY_LABELS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

function pad2(n: number): string { return String(n).padStart(2, '0'); }
// new Date(y, m, d) tự chuẩn hoá tràn (m=12 -> tháng 1 năm sau, d=0 -> ngày cuối tháng trước...) nên
// dùng chung 1 hàm này cho MỌI phép cộng/trừ tháng-ngày thay vì tự nối chuỗi, tránh lỗi "2026-13-01".
function toISO(y: number, m0: number, d: number): string {
  const dt = new Date(y, m0, d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}
function daysInMonth(y: number, m0: number): number { return new Date(y, m0 + 1, 0).getDate(); }
function firstWeekdayMon0(y: number, m0: number): number { return (new Date(y, m0, 1).getDay() + 6) % 7; } // 0=T2..6=CN
function addDaysIso(dateIso: string, days: number): string {
  const [y, m, d] = dateIso.split('-').map(Number);
  return toISO(y, m - 1, d + days);
}

interface Props {
  roomId: string;
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}

export default function StayDatePicker({ roomId, from, to, onChange }: Readonly<Props>) {
  const today = todayISO();
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => Number(from.slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(() => Number(from.slice(5, 7)) - 1);
  const [blocked, setBlocked] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [pickingFrom, setPickingFrom] = useState<string | null>(null);

  const { maxY, maxM0 } = useMemo(() => {
    const d = new Date(); d.setMonth(d.getMonth() + MAX_MONTHS_AHEAD);
    return { maxY: d.getFullYear(), maxM0: d.getMonth() };
  }, []);
  const monthStartIso = toISO(viewYear, viewMonth, 1);
  const atMinMonth = viewYear === Number(today.slice(0, 4)) && viewMonth === Number(today.slice(5, 7)) - 1;
  const atMaxMonth = viewYear === maxY && viewMonth === maxM0;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const rangeFrom = monthStartIso < today ? today : monthStartIso;
    const rangeTo = toISO(viewYear, viewMonth, daysInMonth(viewYear, viewMonth));
    setLoading(true);
    fetch(`/api/sale/timeline?from=${rangeFrom}&to=${rangeTo}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        // Gộp dồn (KHÔNG ghi đè) — nếu đang chọn Trả phòng mà đổi sang tháng khác, dữ liệu khoá của
        // tháng đã xem trước đó vẫn cần giữ để vòng lặp "có đêm nào ở giữa bị khoá" trong handleDayClick
        // kiểm tra đúng cho khoảng ngày đi QUA nhiều tháng.
        setBlocked(prev => {
          const next = new Set(prev);
          for (const c of (d.cells ?? []) as { roomId: string; date: string; blocked: boolean }[]) {
            if (c.roomId === roomId && c.blocked) next.add(c.date);
          }
          return next;
        });
      })
      .catch(e => {
        if (cancelled) return;
        logPublicClientError({ route: 'components/sale/StayDatePicker.tsx', action: 'load_timeline', message: e instanceof Error ? e.message : String(e), details: { roomId, viewYear, viewMonth } });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewYear, viewMonth, roomId]);

  function changeMonth(delta: number) {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  }

  function handleDayClick(dateIso: string) {
    if (dateIso < today || blocked.has(dateIso)) return;
    if (!pickingFrom || dateIso <= pickingFrom) {
      setPickingFrom(dateIso);
      onChange(dateIso, addDaysIso(dateIso, 1));
      return;
    }
    let d = pickingFrom;
    let blockedBetween = false;
    while (d < dateIso) {
      if (blocked.has(d)) { blockedBetween = true; break; }
      d = addDaysIso(d, 1);
    }
    if (blockedBetween) {
      setPickingFrom(dateIso);
      onChange(dateIso, addDaysIso(dateIso, 1));
      return;
    }
    onChange(pickingFrom, dateIso);
    setPickingFrom(null);
    setOpen(false);
  }

  const cells: { dateIso: string | null }[] = [];
  const leadBlanks = firstWeekdayMon0(viewYear, viewMonth);
  for (let i = 0; i < leadBlanks; i++) cells.push({ dateIso: null });
  const totalDays = daysInMonth(viewYear, viewMonth);
  for (let day = 1; day <= totalDays; day++) cells.push({ dateIso: toISO(viewYear, viewMonth, day) });

  return (
    <div>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={() => setOpen(o => !o)}
          className="text-left px-3 py-2 rounded-xl border border-[#e4ddd4] bg-white hover:border-[#c7bdae] transition-colors">
          <div className="text-[10px] text-[#8a7a68]">Nhận phòng</div>
          <div className="text-sm font-medium">{fmtDate(from)}</div>
        </button>
        <button type="button" onClick={() => setOpen(o => !o)}
          className="text-left px-3 py-2 rounded-xl border border-[#e4ddd4] bg-white hover:border-[#c7bdae] transition-colors">
          <div className="text-[10px] text-[#8a7a68]">Trả phòng</div>
          <div className="text-sm font-medium">{fmtDate(to)}</div>
        </button>
      </div>

      {open && (
        <div className="mt-2 border border-[#e4ddd4] rounded-xl p-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <button type="button" onClick={() => changeMonth(-1)} disabled={atMinMonth}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#f0ece5] disabled:opacity-30 disabled:cursor-not-allowed">‹</button>
            <div className="text-sm font-semibold">Tháng {viewMonth + 1}/{viewYear}</div>
            <button type="button" onClick={() => changeMonth(1)} disabled={atMaxMonth}
              className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#f0ece5] disabled:opacity-30 disabled:cursor-not-allowed">›</button>
          </div>

          {pickingFrom && <p className="text-[11px] text-[#8a7a68] mb-1.5">Đã chọn nhận phòng {fmtDate(pickingFrom)} — bấm ngày trả phòng.</p>}

          <div className="grid grid-cols-7 gap-0.5 text-center text-[10px] text-[#8a7a68] mb-1">
            {WEEKDAY_LABELS.map(w => <div key={w}>{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((c, i) => {
              if (!c.dateIso) return <div key={`blank-${i}`} />;
              const dateIso = c.dateIso;
              const isPast = dateIso < today;
              const isBlocked = blocked.has(dateIso);
              const disabled = isPast || isBlocked;
              const inRange = dateIso >= from && dateIso < to;
              const isEndpoint = dateIso === from || dateIso === to;
              return (
                <button key={dateIso} type="button" disabled={disabled} onClick={() => handleDayClick(dateIso)}
                  title={isBlocked ? 'Đã có khách' : undefined}
                  className={`h-8 text-xs rounded-lg flex items-center justify-center transition-colors
                    ${disabled ? 'text-[#c7c1b8] line-through cursor-not-allowed' : 'hover:bg-[#f0ece5] cursor-pointer'}
                    ${!disabled && isEndpoint ? 'bg-[#1c1a16] text-white font-semibold' : ''}
                    ${!disabled && inRange && !isEndpoint ? 'bg-[#f0ece5]' : ''}`}>
                  {Number(dateIso.slice(8, 10))}
                </button>
              );
            })}
          </div>
          {loading && <p className="text-[10px] text-[#8a7a68] mt-1.5">Đang tải lịch trống…</p>}
          <p className="text-[10px] text-[#8a7a68] mt-1.5">Ngày gạch ngang = đã có khách, không đặt được.</p>
        </div>
      )}
    </div>
  );
}
